import React, { useState, useEffect, useMemo } from 'react'
import { Box, CircularProgress, Alert } from '@mui/material'
import { apiService } from '../../services/api'
import {
  ReportShell, HeaderButton, KpiStrip, Section, ReportTable, SubTabs, Pill, Icon, fmt,
} from '../report-shell'

const STATUS_KIND = { VIP: 'good', STANDARD: 'info', ZERO: 'bad' }
const STATUS_BG = {
  VIP:      { bg: '#dcfce7', fg: '#15803d', br: '#86efac4d' },
  STANDARD: { bg: '#dbeafe', fg: '#1d4ed8', br: '#93c5fd4d' },
  ZERO:     { bg: '#fee2e2', fg: '#b91c1c', br: '#fca5a54d' },
}

const PlusVipStandardZero = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [activeKamTab, setActiveKamTab] = useState(0)
  const [exporting, setExporting] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true); setError(null)
      const response = await apiService.getPlusVipStandardZero()
      setData(response.data)
    } catch (err) {
      setError(`Błąd pobierania danych: ${err.message}`)
      setData(null)
    } finally { setLoading(false) }
  }

  useEffect(() => { fetchData() }, [])

  const exportExcel = async () => {
    try {
      setExporting(true)
      const response = await apiService.getPlusVipStandardZeroExport()
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `VIP_Standard_Zero_Plus_${new Date().toISOString().slice(0, 10)}.xlsx`)
      document.body.appendChild(link); link.click(); link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(`Błąd eksportu: ${err.message}`)
    } finally { setExporting(false) }
  }

  const kamTabs = useMemo(() => {
    if (!data) return ['Wszyscy']
    return ['Wszyscy', ...data.kam_summary.map(k => k.kam)]
  }, [data])

  const filteredSalons = useMemo(() => {
    if (!data) return []
    if (activeKamTab === 0) return data.salons
    const selectedKam = kamTabs[activeKamTab]
    return data.salons.filter(s => (s.kam || '').toUpperCase().trim() === selectedKam)
  }, [data, activeKamTab, kamTabs])

  const columns = useMemo(() => {
    if (!data) return []
    const cols = [
      { field: 'sfid', header: 'Id SF', width: '100px', numeric: true },
      { field: 'region', header: 'Region', width: '130px' },
      { field: 'mikrorynek', header: 'Mikrorynek', width: '150px' },
    ]
    if (activeKamTab === 0) {
      cols.push({ field: 'kam', header: 'KAM', width: '110px' })
    }
    cols.push(
      { field: 'miasto', header: 'Miasto', width: '130px' },
      { field: 'adres', header: 'Ulica', width: '170px' },
      { field: 'ch', header: 'CH', width: '130px' },
      { field: 'top10', header: 'TOP 10', width: '90px',
        render: (v) => v ? <Pill kind="info" small>Tak</Pill> : '' },
      { field: 'wymagajacy_uwagi', header: 'Wym. uwagi', width: '110px',
        render: (v) => v ? <Pill kind="warn" small>Tak</Pill> : '' },
    )
    data.month_labels.forEach((label, i) => {
      cols.push({
        field: `__month_${i}`,
        header: label,
        width: '110px',
        sales: true,
      })
    })
    cols.push(
      { field: 'older', header: 'Starsze', width: '95px', numeric: true },
      { field: 'total', header: 'Total',   width: '95px', numeric: true },
      {
        field: 'status', header: 'Status', width: '110px',
        render: (v) => {
          const c = STATUS_BG[v] || { bg: '#f5f5f4', fg: '#57534e', br: 'rgba(0,0,0,0.08)' }
          return (
            <span style={{
              display: 'inline-flex', padding: '2px 8px', borderRadius: 999,
              fontSize: 11, fontWeight: 600,
              background: c.bg, color: c.fg, border: `1px solid ${c.br}`,
            }}>{v}</span>
          )
        },
      },
    )
    return cols
  }, [data, activeKamTab])

  const rows = useMemo(() => filteredSalons.map((s, idx) => {
    const flat = { id: s.sfid || idx, ...s }
    if (data?.month_labels) {
      data.month_labels.forEach((_, i) => { flat[`__month_${i}`] = s.months?.[i] || 0 })
    }
    return flat
  }), [filteredSalons, data])

  const summary = data?.summary || {}
  const kam_summary = data?.kam_summary || []

  const kpiTiles = data ? [
    { key: 'total',    label: 'Salony łącznie',     value: fmt.int(summary.total_salons || 0) },
    { key: 'vip',      label: 'VIP (≥3 szt)',       value: fmt.int(summary.vip_count || 0),      accent: '#16a34a' },
    { key: 'standard', label: 'STANDARD (1-2 szt)', value: fmt.int(summary.standard_count || 0), accent: '#2563eb' },
    { key: 'zero',     label: 'ZERO (0 szt)',       value: fmt.int(summary.zero_count || 0),     accent: '#dc2626' },
  ] : []

  return (
    <ReportShell
      title="Plus — VIP / Standard / Zero"
      subtitle="Klasyfikacja salonów Plus po wolumenie sprzedaży: VIP (≥3 szt), STANDARD (1–2 szt), ZERO (0 szt)."
      breadcrumb={['KAM-SFA', 'Plus', 'VIP / Standard / Zero']}
      accentColor="#00a651"
      actions={
        <HeaderButton primary onClick={exportExcel} disabled={exporting || !data}>
          <Icon.download /> {exporting ? 'Eksport…' : 'Eksport Excel'}
        </HeaderButton>
      }
      kpiStrip={kpiTiles.length > 0 ? <KpiStrip tiles={kpiTiles} /> : null}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      )}
      {!loading && data && (() => {
        const tabsItems = kamTabs.map((tab, i) => {
          if (i === 0) return { value: 0, label: `Wszyscy (${summary.total_salons})` }
          const ks = kam_summary.find(k => k.kam === tab)
          return { value: i, label: `${tab} (${ks?.total_salons || 0})` }
        })
        const ks = activeKamTab > 0 ? kam_summary.find(k => k.kam === kamTabs[activeKamTab]) : null
        const subtitle = ks
          ? `VIP: ${ks.vip_count} · STANDARD: ${ks.standard_count} · ZERO: ${ks.zero_count}`
          : null
        return (
          <Section
            title="Salony"
            subtitle={subtitle}
            right={<SubTabs tabs={tabsItems} value={activeKamTab} onChange={setActiveKamTab} />}
          >
            <ReportTable
              rows={rows}
              columns={columns}
              initialSort={{ field: 'total', dir: 'desc' }}
              pageSize={25}
              pageSizeOptions={[25, 50, 100]}
              density="compact"
            />
          </Section>
        )
      })()}
    </ReportShell>
  )
}

export default PlusVipStandardZero
